import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import ModalWrapper from '../../../components/modals/ModalWrapper'
import ReauthenticationModal from '../../../components/reauthenticationModal/ReauthenticationModal'
import EmailVerificationCodeEntry from '../../../components/modals/emailVerification/EmailVerificationCodeEntry'
import EmailVerificationEmailPasswordForm from '../../../components/modals/emailVerification/EmailVerificationEmailPasswordForm'
import EmailVerificationBackupOffer from '../../../components/modals/emailVerification/EmailVerificationBackupOffer'
import EmailVerificationPrimaryOffer from '../../../components/modals/emailVerification/EmailVerificationPrimaryOffer'
import EmailVerificationAffirmPrimaryOffer from '../../../components/modals/emailVerification/EmailVerificationAffirmPrimaryOffer'
import EmailVerificationAffirmBothOffer from '../../../components/modals/emailVerification/EmailVerificationAffirmBothOffer'
import EmailVerificationUpdateEmailUnavailable from '../../../components/modals/emailVerification/EmailVerificationUpdateEmailUnavailable'
import EmailVerificationStepper from '../../../components/modals/emailVerification/EmailVerificationStepper'
import EmailVerificationEnvelopeIllustration from '../../../components/modals/emailVerification/EmailVerificationEnvelopeIllustration'
import {
  createEmailVerification,
  resendEmailVerification,
  verifyEmailVerification,
  dismissEmailVerificationPrompt,
  affirmPrimaryEmail,
  affirmBackupEmail,
  fetchEmailVerificationPrompts,
} from '../../../actions/emailVerification'
import getErrorMessageForRequest from '../../../utils/getErrorMessageForRequest'
import useEmailVerificationRequest from '../../../utils/emailVerification/useEmailVerificationRequest'
import {
  ADD_BACKUP_EMAIL_DESCRIPTION, UPDATE_BACKUP_EMAIL_DESCRIPTION,
  BACKUP_SAME_AS_ACCOUNT_EMAIL_ERROR, BACKUP_SAME_AS_CURRENT_BACKUP_ERROR,
} from '../../../utils/emailVerification/emailVerificationCopy'
import analytics from '../../../utils/cdpAnalytics'

// "Don't ask me again" on the add-backup offer only shows up once the user
// has dismissed it ("Not now") at least this many times — meant to let
// someone get the ask a few times before offering to silence it forever.
const ADD_BACKUP_DISMISSES_BEFORE_NEVER_ASK_AGAIN = 3

// shared by every code-entry step's helpNote
const CODE_ENTRY_HELP_NOTE = (
  <React.Fragment>
    {"Can't find the email? Please check your spam folder. If you still can't locate the email, contact "}
    <a href='mailto:support@plickers.com'>support@plickers.com</a>
    .
  </React.Fragment>
)

// Steps this container walks through, spanning Flow A (verify/update primary)
// and Flow B (add backup) from email_verification_banner.md:
//   verifyPrimaryOffer -> sendPrimaryCode -> primaryCodeEntry -> [newEmailForm -> newEmailCodeEntry]
//   -> backupOffer -> backupEmailForm -> backupCodeEntry
function EmailVerificationFlowContainer(props) {
  const {
    hideModal, flow, initialStep, user, meta, signinMethods, prompts,
    createEmailVerification, resendEmailVerification, verifyEmailVerification,
    dismissEmailVerificationPrompt, affirmPrimaryEmail, affirmBackupEmail,
    fetchEmailVerificationPrompts,
  } = props

  // lives on the /meta record (state.meta), not the user object
  const addBackupDismissCount = (meta && meta.emailVerificationPromptState &&
    meta.emailVerificationPromptState.addBackupDismissCount) || 0
  const showNeverAskBackup = addBackupDismissCount >= ADD_BACKUP_DISMISSES_BEFORE_NEVER_ASK_AGAIN

  // every step visited so far, current one last — back just pops to the one
  // before it. Completing something (a verify/affirm that chains onward)
  // resets this to the new step, since a finished step can't be gone back into
  const [stepHistory, setStepHistory] = useState([
    initialStep || (flow === 'addBackup' ? 'backupOffer' : 'verifyPrimaryOffer'),
  ])
  const step = stepHistory[stepHistory.length - 1]
  const {
    verification, clearVerification, requestCode, requestLoading, requestError, setRequestError,
    showReauth, reauthPurpose, handleReauthenticate, cancelReauth, submitCode, resend,
  } = useEmailVerificationRequest({
    userId: user.id,
    createEmailVerification,
    resendEmailVerification,
    verifyEmailVerification,
    onCodeSent: (purpose) => {
      if (purpose === 'updatePrimary') {
        goToStep('newEmailCodeEntry')
      } else if (purpose === 'upsertBackup') {
        goToStep('backupCodeEntry')
      } else if (step === 'sendPrimaryCode') {
        // skip sendPrimaryCode's transient loading screen when going back
        replaceStep('primaryCodeEntry')
      } else {
        goToStep('primaryCodeEntry')
      }
    },
  })
  // tracks whether backup is being asked for right after a just-completed
  // verify/update/affirm (vs. reached standalone) — gates the stepper (see showStepper)
  const [justVerifiedPrimary, setJustVerifiedPrimary] = useState(false)
  // true when backupEmailForm/backupCodeEntry were reached via affirmBothOffer's
  // "Edit" on the backup address (replacing an already-verified backup), rather
  // than a fresh add — changes the form and code-entry copy
  const [replacingBackupEmail, setReplacingBackupEmail] = useState(false)
  // true when affirmBothOffer was chained to right after verifying/updating
  // the primary (only the backup was actually due) — the primary was just
  // confirmed, so "still yours?" reads oddly; asks "are these correct?" instead
  const [affirmAfterVerify, setAffirmAfterVerify] = useState(false)
  // once affirmBothOffer has been shown, updating either address from it
  // (via "Edit") always comes back to it afterward, rather than closing or
  // moving on, so the pair is still reviewed and confirmed together
  const affirmBothShownRef = useRef(false)
  if (step === 'affirmBothOffer') affirmBothShownRef.current = true
  // success message ("Account email address verified", ...) shown until the user
  // clicks Close/Continue
  const [verifiedMessage, setVerifiedMessage] = useState('')
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [affirmError, setAffirmError] = useState('')
  // which of handleAffirmBoth's two affirms have already succeeded
  const affirmBothDoneRef = useRef({ primary: false, backup: false })
  // set alongside verifiedMessage whenever a just-completed step is about to
  // chain into another one (vs. just closing) — the step it'll chain to, so
  // the "Close"/disabled "Continue" button can become an active "Continue"
  const [pendingChainStep, setPendingChainStep] = useState(null)

  // this modal can be showing (independently) in more than one tab at once —
  // closing it here (verified, skipped, remind later, etc.) should also
  // close it in any other tab that has it open, not just this one
  function closeModal() {
    hideModal({ onlyIfModal: 'EMAIL_VERIFICATION_FLOW_MODAL', syncActionBC: true })
  }

  function goToStep(nextStep) {
    setRequestError('')
    setStepHistory((history) => [...history, nextStep])
  }

  // swaps out the current step instead of stacking on top of it — for
  // sendPrimaryCode's transient loading screen, which back should skip over
  function replaceStep(nextStep) {
    setRequestError('')
    setStepHistory((history) => [...history.slice(0, -1), nextStep])
  }

  // a just-completed step chaining onward: everything before it is done,
  // so there's nothing to go back to
  function restartFromStep(nextStep) {
    setRequestError('')
    setStepHistory([nextStep])
  }

  function goBack() {
    setRequestError('')
    setStepHistory((history) => (history.length > 1 ? history.slice(0, -1) : history))
  }

  // no back once the current step has succeeded (verifiedMessage is showing)
  // — it's about to close or chain onward
  const onBack = stepHistory.length > 1 && !verifiedMessage ? goBack : undefined

  // the account email has to match a linked Google/Apple sign-in, so those
  // accounts can't change it here (see EmailVerificationUpdateEmailUnavailable)
  const externalSigninName = (signinMethods && signinMethods.google && 'Google') ||
    (signinMethods && signinMethods.apple && 'Apple') || null

  const goToNewEmailForm = () => goToStep(externalSigninName ? 'updateEmailUnavailable' : 'newEmailForm')

  const goToBackupEmailForm = (replacing) => {
    setReplacingBackupEmail(replacing)
    goToStep('backupEmailForm')
  }

  // the transition out of showSuccessThenAddBackup's success state, run by
  // the "Continue" button
  const advanceToBackupEmailForm = () => {
    clearVerification()
    setJustVerifiedPrimary(true)
    setVerifiedMessage('')
    setPendingChainStep(null)
    // this is always a fresh add chained after primary just got sorted out,
    // never a replace
    setReplacingBackupEmail(false)
    // skip the standalone offer screen's copy — they just verified, so go
    // straight to collecting the backup address
    restartFromStep('backupEmailForm')
  }

  // the transition out of showSuccessThenAffirmBoth's success state, run by
  // the "Continue" button
  const advanceToAffirmBothOffer = () => {
    clearVerification()
    setVerifiedMessage('')
    setPendingChainStep(null)
    setAffirmAfterVerify(true)
    // an address may have just changed, so a confirm that succeeded on an
    // earlier partial-failure attempt no longer counts
    affirmBothDoneRef.current = { primary: false, backup: false }
    restartFromStep('affirmBothOffer')
  }

  // every success state (verify/update code entry, and every affirm offer)
  // holds until the Close/Continue button is clicked — see
  // handleConfirmedAction for where that goes
  const showSuccessThenAddBackup = (message) => {
    setVerifiedMessage(message)
    setPendingChainStep('backupEmailForm')
  }

  const showSuccessThenAffirmBoth = (message) => {
    setVerifiedMessage(message)
    setPendingChainStep('affirmBothOffer')
  }

  const showSuccessThenClose = (message) => {
    setVerifiedMessage(message)
  }

  // shared "Continue" handler for every success state (affirmPrimaryOffer's
  // confirmed state, and the verify/update code-entry screens): closes
  // immediately if there's nothing queued up next, or jumps straight into
  // the pending chain step if there is
  const handleConfirmedAction = () => {
    if (pendingChainStep === 'backupEmailForm') {
      advanceToBackupEmailForm()
    } else if (pendingChainStep === 'affirmBothOffer') {
      advanceToAffirmBothOffer()
    } else {
      closeModal()
    }
  }

  // shared by every "what's next" decision point: a full add-backup ask wins
  // over a stale-backup affirm, and if neither is due, fall back (e.g. close
  // right away, or close after showing a message)
  const chainFromPromptsOrFallback = (prompts, message, fallback) => {
    if (prompts && prompts.addBackup) {
      showSuccessThenAddBackup(message)
    } else if (prompts && prompts.affirmBackup && user.backupEmail) {
      showSuccessThenAffirmBoth(message)
    } else {
      fallback()
    }
  }

  const sendPrimaryCode = () => requestCode({ purpose: 'verifyPrimary' })

  // only run for the initial step, on mount
  useEffect(() => {
    if (step === 'sendPrimaryCode') {
      sendPrimaryCode()
    }
  }, [])

  const handleVerifyPrimaryCode = async (code) => {
    const response = await submitCode(code)
    analytics.track('Email verification flow action', { flow: 'verifyPrimary', action: 'verified' })
    chainFromPromptsOrFallback(
      response.prompts,
      'Account email address verified',
      () => showSuccessThenClose('Account email address verified'),
    )
  }

  const handleSubmitNewEmail = ({ email }) => requestCode({ purpose: 'updatePrimary', email })

  const handleVerifyNewEmailCode = async (code) => {
    const response = await submitCode(code)
    analytics.track('Email verification flow action', { flow: 'verifyPrimary', action: 'updated' })
    // updating the primary to the backup's address retires the backup, so
    // there'd be no pair left to confirm — fall through to the usual chain
    if (affirmBothShownRef.current && !response.backupEmailRemoved) {
      showSuccessThenAffirmBoth('Account email address updated')
      return
    }
    chainFromPromptsOrFallback(
      response.prompts,
      'Account email address updated',
      () => showSuccessThenClose('Account email address updated'),
    )
  }

  const handleSubmitBackupEmail = ({ email }) => requestCode({ purpose: 'upsertBackup', email })

  const handleVerifyBackupCode = async (code) => {
    const response = await submitCode(code)
    analytics.track('Email verification flow action', { flow: 'addBackup', action: 'verified' })
    const message = replacingBackupEmail ? 'Backup email address updated' : 'Backup email address added'
    // also covers a primary still due without affirmBothOffer having been
    // shown yet, so it isn't left hanging
    if (affirmBothShownRef.current || (response.prompts && response.prompts.affirmPrimary)) {
      showSuccessThenAffirmBoth(message)
    } else {
      showSuccessThenClose(message)
    }
  }

  // every "Not now" / "Remind me later" / "Don't ask me again": closes right
  // away without waiting on the dismiss request — the user asked to be left
  // alone, and a failed dismiss just means the prompt stays due and comes
  // back next session
  const dismissThenClose = (dismissRequest) => {
    dismissRequest.catch(() => {})
    closeModal()
  }

  // shared by backupOffer and backupEmailForm, fresh add only — replacing a
  // backup (via affirmBothOffer's "Edit") has no "Not now", just back
  const handleSkipBackup = () => {
    dismissThenClose(dismissEmailVerificationPrompt(user.id, 'addBackup'))
  }

  const handleNeverAskBackup = () => {
    analytics.track('Email verification flow action', { flow: 'addBackup', action: 'neverAskAgain' })
    dismissThenClose(dismissEmailVerificationPrompt(user.id, 'addBackup', { never: true }))
  }

  const handleRemindPrimaryLater = () => {
    analytics.track('Email verification flow action', { flow: 'verifyPrimary', action: 'remindLater' })
    dismissThenClose(dismissEmailVerificationPrompt(user.id, 'verifyPrimary'))
  }

  const handleRemindAffirmLater = () => {
    analytics.track('Email verification flow action', { flow: 'affirmPrimary', action: 'remindLater' })
    dismissThenClose(dismissEmailVerificationPrompt(user.id, 'affirmPrimary'))
  }

  const handleAffirmPrimary = async () => {
    setConfirmLoading(true)
    setAffirmError('')
    try {
      await affirmPrimaryEmail(user.id)
      analytics.track('Email verification flow action', { flow: 'affirmPrimary', action: 'confirmed' })
      const refreshedPrompts = await fetchEmailVerificationPrompts(user.id)
      setConfirmLoading(false)
      chainFromPromptsOrFallback(
        refreshedPrompts, 'Email address confirmed', () => showSuccessThenClose('Email address confirmed'),
      )
    } catch (err) {
      setConfirmLoading(false)
      setAffirmError(getErrorMessageForRequest({ requestName: 'affirmPrimaryEmail', response: err.response }))
    }
  }

  const handleRemindAffirmBothLater = () => {
    analytics.track('Email verification flow action', { flow: 'affirmBoth', action: 'remindLater' })
    // this screen also shows when only one of the two is due — only dismiss
    // what's actually due, so the other's schedule isn't touched
    const dueNames = ['affirmPrimary', 'affirmBackup']
      .filter((name) => prompts && prompts[name])
    const names = dueNames.length ? dueNames : ['affirmPrimary', 'affirmBackup']
    dismissThenClose(Promise.all(
      names.map((name) => dismissEmailVerificationPrompt(user.id, name)),
    ))
  }

  // both are lightweight "just confirm" affirms, so a single click fires
  // them concurrently instead of walking through two sequential screens —
  // always both, even when the screen was opened for only one of them.
  // Each one's success is remembered in affirmBothDoneRef so if only one
  // fails, retrying just redoes that one, and the error shown is that one's
  const handleAffirmBoth = async () => {
    setConfirmLoading(true)
    setAffirmError('')
    const done = affirmBothDoneRef.current
    const affirmOnce = (key, affirm) => (done[key] ? null : affirm(user.id).then(
      () => { done[key] = true; return null },
      (err) => err,
    ))
    const [primaryErr, backupErr] = await Promise.all([
      affirmOnce('primary', affirmPrimaryEmail),
      affirmOnce('backup', affirmBackupEmail),
    ])
    if (primaryErr || backupErr) {
      setConfirmLoading(false)
      setAffirmError(primaryErr ?
        getErrorMessageForRequest({ requestName: 'affirmPrimaryEmail', response: primaryErr.response }) :
        getErrorMessageForRequest({ requestName: 'affirmBackupEmail', response: backupErr.response }))
      return
    }
    analytics.track('Email verification flow action', { flow: 'affirmBoth', action: 'confirmed' })
    // refresh so the store doesn't keep a stale affirmPrimary/affirmBackup: true
    await fetchEmailVerificationPrompts(user.id)
    setConfirmLoading(false)
    showSuccessThenClose('Email addresses confirmed')
  }

  function renderContent() {
    switch (step) {
      case 'verifyPrimaryOffer':
        return (
          <EmailVerificationPrimaryOffer
            email={user.email}
            onSendCode={sendPrimaryCode}
            onDifferentEmail={goToNewEmailForm}
            onRemindLater={handleRemindPrimaryLater}
            sendCodeLoading={requestLoading}
            errorMessage={requestError}
          />
        )

      case 'affirmPrimaryOffer':
        return (
          <EmailVerificationAffirmPrimaryOffer
            email={user.email}
            onAffirm={handleAffirmPrimary}
            onDifferentEmail={goToNewEmailForm}
            onRemindLater={handleRemindAffirmLater}
            onConfirmedAction={handleConfirmedAction}
            confirmedActionLabel={pendingChainStep ? 'Continue' : 'Close'}
            confirmLoading={confirmLoading}
            confirmedMessage={verifiedMessage}
            errorMessage={affirmError}
          />
        )

      case 'affirmBothOffer':
        return (
          <EmailVerificationAffirmBothOffer
            title={affirmAfterVerify ? 'Is this information correct?' : undefined}
            affirmLabel={affirmAfterVerify ? 'Yes, it is' : undefined}
            primaryEmail={user.email}
            backupEmail={user.backupEmail}
            onAffirm={handleAffirmBoth}
            onDifferentPrimaryEmail={goToNewEmailForm}
            onDifferentBackupEmail={() => goToBackupEmailForm(true)}
            onRemindLater={handleRemindAffirmBothLater}
            onConfirmedAction={handleConfirmedAction}
            confirmedActionLabel='Close'
            confirmLoading={confirmLoading}
            confirmedMessage={verifiedMessage}
            errorMessage={affirmError}
          />
        )

      case 'sendPrimaryCode':
        return (
          <div className='emailVerificationFlow-loading'>
            {requestLoading && (
              <div className='emailVerificationFlow-loading-message'>Sending code to your email...</div>
            )}
            {requestError && (
              <React.Fragment>
                <div className='emailVerificationFlow-error'>{requestError}</div>
                <button type='button' className='btn btn--blue' onClick={sendPrimaryCode}>Try again</button>
                <button
                  type='button'
                  className='emailVerificationFlow-linkBtn'
                  onClick={() => replaceStep('verifyPrimaryOffer')}
                >
                  ← Back
                </button>
              </React.Fragment>
            )}
          </div>
        )

      case 'primaryCodeEntry':
        return (
          <EmailVerificationCodeEntry
            email={(verification && verification.email) || user.email}
            title='Please verify your email address'
            description='Please enter the code we just sent to your email address'
            resendAvailableAt={verification && verification.resendAvailableAt}
            verifiedMessage={verifiedMessage}
            onConfirmedAction={handleConfirmedAction}
            confirmedActionLabel={pendingChainStep ? 'Continue' : 'Close'}
            onSubmitCode={handleVerifyPrimaryCode}
            onResend={resend}
            onBack={onBack}
            helpNote={CODE_ENTRY_HELP_NOTE}
          />
        )

      case 'newEmailForm':
        return (
          <EmailVerificationEmailPasswordForm
            title='Update account email address'
            description="Don't have access to your account email address anymore? Update now. We'll send a code to your new email address to complete the change."
            emailLabel='New email address'
            submitLabel='Send code'
            currentEmail={user.email}
            backupEmail={user.backupEmail}
            submitLoading={requestLoading}
            errorMessage={requestError}
            onSubmit={handleSubmitNewEmail}
            onBack={onBack}
          />
        )

      case 'updateEmailUnavailable':
        return (
          <EmailVerificationUpdateEmailUnavailable
            externalSigninName={externalSigninName}
            onBack={onBack}
            onGoToAccountPage={() => closeModal()}
          />
        )

      case 'newEmailCodeEntry':
        return (
          <EmailVerificationCodeEntry
            email={verification && verification.email}
            title='Verify your new email address'
            description="We've sent a code to your new email address. Please enter the code below to verify."
            resendAvailableAt={verification && verification.resendAvailableAt}
            verifiedMessage={verifiedMessage}
            onConfirmedAction={handleConfirmedAction}
            confirmedActionLabel={pendingChainStep ? 'Continue' : 'Close'}
            onSubmitCode={handleVerifyNewEmailCode}
            onResend={resend}
            onBack={onBack}
            helpNote={CODE_ENTRY_HELP_NOTE}
          />
        )

      case 'backupOffer':
        return (
          <EmailVerificationBackupOffer
            onAddBackup={() => goToBackupEmailForm(false)}
            onSkip={handleSkipBackup}
            onNeverAskAgain={handleNeverAskBackup}
            showNeverAskAgain={showNeverAskBackup}
          />
        )

      case 'backupEmailForm':
        return (
          <EmailVerificationEmailPasswordForm
            title={replacingBackupEmail ? 'Update backup email address' : 'Add a backup email address'}
            description={replacingBackupEmail ?
              UPDATE_BACKUP_EMAIL_DESCRIPTION : ADD_BACKUP_EMAIL_DESCRIPTION}
            showIllustration={false}
            emailLabel='Backup email address'
            submitLabel={replacingBackupEmail ? 'Update backup' : 'Add backup'}
            currentEmail={user.email}
            duplicateEmailError={BACKUP_SAME_AS_ACCOUNT_EMAIL_ERROR}
            currentBackupEmail={replacingBackupEmail ? user.backupEmail : undefined}
            sameAsCurrentBackupError={BACKUP_SAME_AS_CURRENT_BACKUP_ERROR}
            submitLoading={requestLoading}
            errorMessage={requestError}
            onSubmit={handleSubmitBackupEmail}
            onBack={onBack}
            // when replacing, "Not now" would read as "don't update after all"
            // but close the whole modal — back (to affirmBothOffer, whose
            // "Remind me later" snoozes everything due) covers that instead
            onSkip={replacingBackupEmail ? undefined : handleSkipBackup}
            // the chained verify-primary path skips backupOffer and lands here
            // directly, so offer "Don't ask me again" here too — only for a
            // fresh add, since it dismisses addBackup (not affirmBackup)
            onNeverAskAgain={
              !replacingBackupEmail && showNeverAskBackup ? handleNeverAskBackup : undefined
            }
          />
        )

      case 'backupCodeEntry':
        return (
          <EmailVerificationCodeEntry
            email={verification && verification.email}
            title={replacingBackupEmail ? 'Verify your new backup email address' : 'Verify your backup email address'}
            description={replacingBackupEmail ? 'Please enter the code we just sent to your new backup email address' : 'Please enter the code we just sent to your email address'}
            resendAvailableAt={verification && verification.resendAvailableAt}
            showIllustration={false}
            verifiedMessage={verifiedMessage}
            onConfirmedAction={handleConfirmedAction}
            confirmedActionLabel={pendingChainStep ? 'Continue' : 'Close'}
            onSubmitCode={handleVerifyBackupCode}
            onResend={resend}
            onBack={onBack}
            helpNote={CODE_ENTRY_HELP_NOTE}
          />
        )

      default:
        return null
    }
  }

  const onBackupSteps = ['backupOffer', 'backupEmailForm', 'backupCodeEntry'].includes(step)
  // the chained verify-primary -> add-backup journey gets the 2-step progress
  // stepper; reaching these same steps standalone (primary already verified,
  // e.g. the addBackup entry point) gets a plain verified-email icon instead —
  // there's no "step 1" to show progress against, just a confirmation of why
  // they're being asked
  const showStepper = justVerifiedPrimary && onBackupSteps
  const showVerifiedIcon = !justVerifiedPrimary && onBackupSteps

  return (
    <div className='emailVerificationFlow-modalContainer marketing'>
      <ModalWrapper
        // dismiss must be explicit (Remind me later / Skip / Not now), not a
        // stray click on the backdrop or the (otherwise decorative) close icon
        hideModal={() => {}}
        closeBtn={false}
        showModalHeader={false}
        modalStyle='dark'
      >
        <div className='emailVerificationFlow-modalContent'>
          {showStepper && <EmailVerificationStepper backupVerified={!!verifiedMessage} />}
          {showVerifiedIcon && <EmailVerificationEnvelopeIllustration />}
          {renderContent()}
        </div>
      </ModalWrapper>
      {showReauth && (
        <ReauthenticationModal
          title={reauthPurpose === 'updatePrimary' ? 'Update Account Email Address' : 'Add a Backup Email Address'}
          // updatePrimary only accepts a password (and only password-only
          // accounts can reach it — see goToNewEmailForm)
          signinMethodsToDisplay={reauthPurpose === 'updatePrimary' ? { password: true } : signinMethods}
          onAuthenticate={handleReauthenticate}
          hideModal={cancelReauth}
        />
      )}
    </div>
  )
}

export default connect(
  (state) => ({
    user: state.user,
    meta: state.meta,
    signinMethods: state.signinMethods,
    prompts: state.emailVerification.prompts,
  }),
  {
    createEmailVerification,
    resendEmailVerification,
    verifyEmailVerification,
    dismissEmailVerificationPrompt,
    affirmPrimaryEmail,
    affirmBackupEmail,
    fetchEmailVerificationPrompts,
  },
)(EmailVerificationFlowContainer)
